import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { GraphProps, BAR_COLOR, PIE_COLORS } from "./graphUtils";

/**
 * SimpleGraph
 * Used for query_type: "simple" | "basic"
 *
 * bar → horizontal bar chart  (identical to original GraphPopupModal)
 * pie → donut chart canvas    (legend is rendered by GraphPopupModal, not here)
 */

/**
 * Compute dense rank for an array of numeric values (descending).
 * Identical values share the same rank; the next distinct value gets
 * rank = previous_rank + 1  (not previous_rank + count_of_ties).
 */
function computeDenseRanks(values: number[]): number[] {
  const sorted = [...new Set(values)].sort((a, b) => b - a);
  const rankMap = new Map<number, number>();
  sorted.forEach((v, i) => rankMap.set(v, i + 1));
  return values.map((v) => rankMap.get(v) ?? 1);
}

const SimpleGraph: React.FC<GraphProps> = ({
  chartData,
  chartType,
  activeIndex,
  setActiveIndex,
  chartReady,
  total,
}) => {

  const denseRanks = useMemo(
    () => computeDenseRanks(chartData.map((d) => d.value)),
    [chartData]
  );

  // Calculate dynamic height based on number of data points to keep bar height constant
  const chartHeight = Math.max(400, chartData.length * 28);

  // ── Horizontal Bar (identical options to original + rank annotations) ────
  const barOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: true, speed: 500 },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: "end",
        barHeight: 20,
        dataLabels: { position: "center" },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "middle",
      style: { fontSize: "11px", fontWeight: 700, colors: ["#000"] },
      offsetX: 0,
      formatter: (val: number) =>
        val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val),
    },
    fill: { type: "solid", colors: [BAR_COLOR] },
    states: { hover: { filter: { type: "darken", value: 0.85 } } },
    dropShadow: {
      enabled: true, top: 4, left: 4, blur: 6,
      color: "rgba(123,111,212,0.35)", opacity: 0.8,
    },
    grid: {
      borderColor: "#f0f0f0",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
categories: chartData.map((d, i) => {
  const rank = String(denseRanks[i]).padStart(2, "0");
  return `#${rank} - ${d.name}`;
}),
      labels: {
        style: { colors: "#999", fontSize: "11px" },
        formatter: (val: string) => {
          const n = Number(val);
          return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
        },
      },
      axisBorder: { color: "#ccc" },
      axisTicks: { show: false },
      title: {
        text: "Values",
        style: { fontSize: "11px", color: "#888", fontWeight: 600 },
        offsetY: 8,
      },
    },
    yaxis: {
      title: {
        text: "Names",
        style: { fontSize: "11px", color: "#888", fontWeight: 500 },
      },
      labels: {
  align: "left",
  style: {
    colors: "#444",
    fontSize: "12px",
    fontFamily: "monospace",
  },
  maxWidth: 260,
},
    },
    // Dense-rank badge rendered just past the right edge of each bar

    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex }: any) => {
        const item = chartData[dataPointIndex];
        const name = item?.name ?? "";
        const team = item?.team ?? "";
        const val = series[seriesIndex][dataPointIndex];
        const rank = denseRanks[dataPointIndex];
        return `
          <div style="background:#fff;border:1px solid #ddd;border-radius:8px;padding:8px 14px;font-size:13px;box-shadow:0 4px 12px rgba(0,0,0,0.1)">
            <p style="margin:0;font-weight:700;color:#333;margin-bottom:2px">${name}</p>
            ${team ? `<p style="margin:0;font-size:11px;color:#888;margin-bottom:4px">${team}</p>` : ""}
            <p style="margin:0;color:${BAR_COLOR};font-weight:600">${Number(val).toLocaleString()}</p>
            <p style="margin:2px 0 0;font-size:10px;color:#aaa">Dense Rank: <strong style="color:${BAR_COLOR}">#${rank}</strong></p>
          </div>`;
      },
    },
    series: [{ name: "Value", data: chartData.map((d) => d.value) }],
  };

  // ── Donut (identical options to original) ───────────────────────────────
  const donutOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: { enabled: true, delay: 120 },
        dynamicAnimation: { enabled: true, speed: 400 },
      },
      events: {
        dataPointMouseEnter: (_e: any, _chart: any, opts: any) =>
          setActiveIndex(opts.dataPointIndex),
        dataPointMouseLeave: () => setActiveIndex(null),
      },
    },
    labels: chartData.map((d) => d.name),
    colors: PIE_COLORS,
    dataLabels: { enabled: false },
    legend: { show: false },
    stroke: { width: 3, colors: ["#ffffff"] },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: true, fontSize: "11px", fontWeight: 700, color: "#888", offsetY: -6,
              formatter: () =>
                activeIndex !== null
                  ? chartData[activeIndex]?.name ?? "Total"
                  : "Total",
            },
            value: {
              show: true, fontSize: "24px", fontWeight: 800,
              color: activeIndex !== null
                ? PIE_COLORS[activeIndex % PIE_COLORS.length]
                : "#444",
              offsetY: 6,
              formatter: (val: string) => Number(val).toLocaleString(),
            },
            total: {
              show: true, showAlways: true,
              label: activeIndex !== null
                ? chartData[activeIndex]?.name ?? "Total"
                : "Total",
              fontSize: "11px", fontWeight: 600, color: "#aaa",
              formatter: () =>
                activeIndex !== null
                  ? chartData[activeIndex]?.value?.toLocaleString() ?? ""
                  : total.toLocaleString(),
            },
          },
        },
        expandOnClick: true,
        startAngle: -90,
        endAngle: 270,
      },
    },
    dropShadow: {
      enabled: true, top: 4, left: 4, blur: 12,
      color: "#6C63FF", opacity: 0.15,
    },
    states: {
      hover: { filter: { type: "lighten", value: 0.15 } },
      active: { filter: { type: "lighten", value: 0.1 } },
    },
    tooltip: {
      custom: ({ series, seriesIndex, w }: any) => {
        const name = w.globals.labels[seriesIndex];
        const val = series[seriesIndex];
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
        const color = PIE_COLORS[seriesIndex % PIE_COLORS.length];
        return `
          <div style="
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            border: 2px solid ${color};
            border-radius: 12px;
            padding: 12px 18px;
            font-size: 13px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 0 0 1px ${color}33;
            min-width: 150px;">
            <p style="margin:0;font-weight:700;color:#f0f0f0;margin-bottom:6px;font-size:12px;letter-spacing:0.3px">${name}</p>
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
              <div style="width:10px;height:10px;border-radius:50%;background:${color};box-shadow:0 0 8px ${color};"></div>
              <p style="margin:0;color:${color};font-weight:800;font-size:16px">${Number(val).toLocaleString()}</p>
            </div>
            <div style="display:flex;align-items:center;gap:6px;background:${color}22;border-radius:6px;padding:3px 7px;">
              <span style="color:${color};font-size:11px;font-weight:700">${pct}%</span>
              <span style="color:#888;font-size:10px">of total</span>
            </div>
          </div>`;
      },
    },
    series: chartData.map((d) => d.value),
  };

  // ── Bar ─────────────────────────────────────────────────────────────────
  if (chartType === "bar") {
    return (
      <div style={{ width: "100%", height: chartHeight }}>
        {chartReady && (
          <ReactApexChart
            type="bar"
            options={barOptions}
            series={barOptions.series as any}
            height={chartHeight}
          />
        )}
      </div>
    );
  }

  // ── Donut canvas only (legend rendered by GraphPopupModal) ──────────────
  return (
    <div style={{
      flex: "0 0 340px", height: 360,
      position: "relative",
      animation: "gp-donutEntrance 0.7s cubic-bezier(0.34,1.56,0.64,1) both",
    }}>
      {chartReady && (
        <ReactApexChart
          type="donut"
          options={donutOptions}
          series={donutOptions.series as any}
          height={360}
        />
      )}
    </div>
  );
};

export default SimpleGraph;